import { useCallback, useRef, useState } from 'react';
import { Icon } from './Icon.jsx';
import { useT } from '../i18n/index.js';
import { comparePdfs } from '../utils/pdfDiff.js';

function FileSlot({ side, file, onPick, label }) {
  const t = useT();
  const inputRef = useRef(null);
  const [hover, setHover] = useState(false);

  const handleFiles = (files) => {
    const f = Array.from(files || []).find(
      (x) => x.type === 'application/pdf' || x.name.toLowerCase().endsWith('.pdf')
    );
    if (f) onPick(f);
  };

  return (
    <div
      className={`cmp-slot ${side} ${hover ? 'hover' : ''} ${file ? 'filled' : ''}`}
      onDragEnter={(e) => { e.preventDefault(); setHover(true); }}
      onDragOver={(e) => { e.preventDefault(); setHover(true); }}
      onDragLeave={() => setHover(false)}
      onDrop={(e) => { e.preventDefault(); setHover(false); handleFiles(e.dataTransfer.files); }}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
    >
      <span className={`cmp-tag cmp-tag-${side}`}>{label}</span>
      {file ? (
        <>
          <Icon name="doc" size={22} />
          <span className="cmp-slot-name" title={file.name}>{file.name}</span>
          <span className="tiny muted">{t('compare.click_replace')}</span>
        </>
      ) : (
        <>
          <Icon name="upload" size={22} />
          <span>{t('compare.drop_here')}</span>
        </>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="sr-only"
        aria-label={label}
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}

// Render one side of a word-level diff. side 'a' shows same+removed
// words (removed in red), side 'b' shows same+added (added in green).
function WordDiff({ words, side }) {
  return (
    <span>
      {words.map((w, i) => {
        if (w.type === 'same') return <span key={i}>{w.text}</span>;
        if (side === 'a' && w.type === 'removed') {
          return <span key={i} className="cmp-w-removed">{w.text}</span>;
        }
        if (side === 'b' && w.type === 'added') {
          return <span key={i} className="cmp-w-added">{w.text}</span>;
        }
        return null;
      })}
    </span>
  );
}

export function CompareView() {
  const t = useT();
  const [fileA, setFileA] = useState(null);
  const [fileB, setFileB] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [hideSame, setHideSame] = useState(false);

  const run = useCallback(async () => {
    if (!fileA || !fileB) return;
    setBusy(true);
    setError('');
    setResult(null);
    setProgress(0);
    try {
      const bytesA = new Uint8Array(await fileA.arrayBuffer());
      const bytesB = new Uint8Array(await fileB.arrayBuffer());
      const res = await comparePdfs(bytesA, bytesB, (done, total) => {
        setProgress(total ? done / total : 0);
      });
      setResult(res);
    } catch (e) {
      console.error(e);
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, [fileA, fileB]);

  const reset = () => {
    setFileA(null);
    setFileB(null);
    setResult(null);
    setError('');
  };

  const visibleRows = result
    ? result.rows.filter((r) => !hideSame || r.type !== 'same')
    : [];

  // Build the render list with page-change markers interleaved.
  const renderItems = [];
  if (result) {
    let lastA = -1;
    let lastB = -1;
    let markedA = -1;
    let markedB = -1;
    for (let i = 0; i < visibleRows.length; i++) {
      const r = visibleRows[i];
      if (r.a) lastA = r.a.page;
      if (r.b) lastB = r.b.page;
      if (lastA !== markedA || lastB !== markedB) {
        renderItems.push({ kind: 'marker', a: lastA, b: lastB, key: `m${i}` });
        markedA = lastA;
        markedB = lastB;
      }
      renderItems.push({ kind: 'row', row: r, key: `r${i}` });
    }
  }

  return (
    <div className="compare-view">
      <div className="cmp-head">
        <h1>{t('compare.title')}</h1>
        <span className="sub">{t('compare.subtitle')}</span>
      </div>

      <div className="cmp-slots">
        <FileSlot side="a" file={fileA} onPick={setFileA} label={t('compare.before')} />
        <div className="cmp-vs"><Icon name="merge" size={18} /></div>
        <FileSlot side="b" file={fileB} onPick={setFileB} label={t('compare.after')} />
      </div>

      <div className="cmp-actions">
        <button className="btn primary" onClick={run} disabled={!fileA || !fileB || busy}>
          <Icon name="selectAll" size={15} />
          {busy ? t('compare.comparing') : t('compare.run')}
        </button>
        {(fileA || fileB || result) && (
          <button className="btn ghost" onClick={reset} disabled={busy}>
            <Icon name="close" size={15} /> {t('compare.reset')}
          </button>
        )}
        {busy && (
          <div className="cmp-progress">
            <div className="cmp-progress-bar" style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>
        )}
      </div>

      {error && <div className="cmp-error">{t('toast.error', { msg: error })}</div>}

      {result && (
        <>
          <div className="cmp-summary">
            <span className="chip">{t('compare.pages_a', { n: result.countA })}</span>
            <span className="chip">{t('compare.pages_b', { n: result.countB })}</span>
            <span className={`chip ${result.changed ? 'chip-warn' : 'chip-ok'}`}>
              {result.changed ? t('compare.diff_count', { n: result.changed }) : t('compare.identical')}
            </span>
            <span className="cmp-legend">
              <span className="cmp-key cmp-key-removed" /> {t('compare.legend_removed')}
              <span className="cmp-key cmp-key-added" /> {t('compare.legend_added')}
            </span>
            <span style={{ marginLeft: 'auto' }} />
            <label className="cmp-toggle">
              <input type="checkbox" checked={hideSame} onChange={(e) => setHideSame(e.target.checked)} />
              {t('compare.hide_same')}
            </label>
          </div>

          <div className="cmp-diff">
            {renderItems.length === 0 && (
              <div className="cmp-empty">{t('compare.no_diff_lines')}</div>
            )}
            {renderItems.map((item) =>
              item.kind === 'marker' ? (
                <div key={item.key} className="cmp-page-marker">
                  {t('compare.page_pair', {
                    a: Math.max(0, item.a) + 1,
                    b: Math.max(0, item.b) + 1,
                  })}
                </div>
              ) : (
                <DiffRow key={item.key} row={item.row} />
              )
            )}
          </div>
        </>
      )}
    </div>
  );
}

function DiffRow({ row }) {
  const r = row;
  return (
    <div className={`cmp-row cmp-row-${r.type}`}>
      <div className="cmp-gutter">
        {r.type === 'removed' ? '−' : r.type === 'added' ? '+' : r.type === 'changed' ? '~' : ''}
      </div>
      <div className="cmp-side-a">
        {r.type === 'changed'
          ? <WordDiff words={r.words} side="a" />
          : r.a && <span>{r.a.text}</span>}
      </div>
      <div className="cmp-side-b">
        {r.type === 'changed'
          ? <WordDiff words={r.words} side="b" />
          : r.b && <span>{r.b.text}</span>}
      </div>
    </div>
  );
}
